export type FleetTodoStatus = "pending" | "in_progress" | "completed" | "cancelled";

export type FleetTodoItem = {
  id: string;
  content: string;
  status: FleetTodoStatus;
  validationNotes?: string[];
  failureReason?: string;
  deliverablePath?: string;
  artifacts?: string[];
};

function normalizeFleetTodoStatus(raw: unknown): FleetTodoStatus {
  if (typeof raw !== "string") {
    return "pending";
  }
  const value = raw.trim().toLowerCase();
  if (value === "pending" || value === "todo" || value === "open") {
    return "pending";
  }
  if (value === "in_progress" || value === "in-progress" || value === "running") {
    return "in_progress";
  }
  if (value === "completed" || value === "done" || value === "success") {
    return "completed";
  }
  if (value === "cancelled" || value === "canceled" || value === "failed" || value === "error") {
    return "cancelled";
  }
  return "pending";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function parseFleetTodoItems(raw: unknown): FleetTodoItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: FleetTodoItem[] = [];
  for (const item of raw) {
    if (!isRecord(item)) {
      continue;
    }
    const id = typeof item.id === "string" ? item.id.trim() : "";
    const content = typeof item.content === "string" ? item.content.trim() : "";
    const status = normalizeFleetTodoStatus(item.status);
    if (id && content) {
      const validationNotes = Array.isArray(item.validationNotes)
        ? item.validationNotes.filter(
            (n): n is string => typeof n === "string" && n.trim().length > 0,
          )
        : undefined;
      const artifacts = Array.isArray(item.artifacts)
        ? item.artifacts.filter((n): n is string => typeof n === "string" && n.trim().length > 0)
        : undefined;
      const failureReason =
        typeof item.failureReason === "string" && item.failureReason.trim()
          ? item.failureReason.trim()
          : undefined;
      const deliverablePath =
        typeof item.deliverablePath === "string" && item.deliverablePath.trim()
          ? item.deliverablePath.trim()
          : undefined;
      out.push({
        id,
        content,
        status,
        ...(validationNotes && validationNotes.length > 0 ? { validationNotes } : {}),
        ...(failureReason ? { failureReason } : {}),
        ...(deliverablePath ? { deliverablePath } : {}),
        ...(artifacts && artifacts.length > 0 ? { artifacts } : {}),
      });
    }
  }
  return out;
}

export function parseFleetTodosUpdated(payload: unknown): {
  sessionKey: string;
  todos: FleetTodoItem[];
} | null {
  if (!isRecord(payload)) {
    return null;
  }
  const sessionKey = typeof payload.sessionKey === "string" ? payload.sessionKey.trim() : "";
  const todos = parseFleetTodoItems(payload.todos);
  if (!sessionKey) {
    return null;
  }
  return { sessionKey, todos };
}
